import { useState, useEffect } from "react"
import { Link } from "react-router-dom"
import { SectionHeader } from "components/ui/section-header"
import type { WidgetProps } from "lib/widget-types"
import { ChevronRight } from "lucide-react"
import { getTodayAppointments } from "services/appointments"

interface AgendaEvent {
  id: string
  title: string
  time: string
  subtitle: string
}

export function TodayAgendaWidget({ widget, refreshKey = 0 }: { widget: WidgetProps["widget"], refreshKey?: number }) {
  const limit = widget.config?.limit ?? 3
  const [events, setEvents] = useState<AgendaEvent[]>([])
  const [loading, setLoading] = useState(true)
  const [hoveredId, setHoveredId] = useState<string | null>(null)

  useEffect(() => {
    const fetchTodayEvents = async () => {
      try {
        const response = await getTodayAppointments()
        const appointments = Array.isArray(response.data) ? response.data : []

        const formattedEvents: AgendaEvent[] = appointments.map((apt: any) => ({
          id: String(apt.id),
          title: apt.title || apt.subject || "Evento",
          time: apt.date && apt.start_time
            ? new Date(`${apt.date}T${apt.start_time}`).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })
            : "-",
          subtitle: apt.description || apt.location || "",
        }))

        setEvents(formattedEvents)
      } catch (error) {
        console.error("Error fetching today events:", error)
        setEvents([])
      } finally {
        setLoading(false)
      }
    }

    fetchTodayEvents()
  }, [refreshKey])

  const displayEvents = events.slice(0, limit)

  return (
    <div
      className="rounded-xl backdrop-blur-sm p-4 border hexa-card-shadow h-full flex flex-col"
      style={{
        backgroundColor: "var(--hexa-card-bg)",
        borderColor: "var(--hexa-border)",
      }}
    >
      <Link to="/agenda" className="cursor-pointer flex-shrink-0">
        <SectionHeader title={widget.title || "Agenda di Oggi"} showChevron />
      </Link>
      <div className="flex-1 min-h-0 overflow-y-auto space-y-2 pr-1 scrollbar-hexa">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <span className="text-sm" style={{ color: "var(--hexa-text-secondary)" }}>Caricamento...</span>
          </div>
        ) : displayEvents.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full">
            <div
              className="flex items-center justify-center mb-2.5"
              style={{
                width: 34,
                height: 34,
                borderRadius: 10,
                backgroundColor: "var(--hexa-empty-icon-bg, rgba(123, 63, 255, 0.1))",
              }}
            >
              <svg
                width="17"
                height="17"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ color: "var(--hexa-empty-icon-color, #7b3fff)", opacity: "var(--hexa-empty-icon-opacity, 0.7)" }}
              >
                <path d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
              </svg>
            </div>
            <span
              className="text-center"
              style={{ fontSize: 11, color: "var(--hexa-empty-text-color, rgba(255,255,255,0.4))" }}
            >
              Nessun evento per oggi
            </span>
          </div>
        ) : (
          displayEvents.map((event) => {
            const isHovered = hoveredId === event.id
            return (
              <Link
                key={event.id}
                to="/agenda"
                className="block border rounded-lg p-2.5 transition-colors"
                style={{
                  backgroundColor: isHovered ? "var(--hexa-sidebar-item-hover)" : "var(--hexa-card-bg)",
                  borderColor: "var(--hexa-border-subtle)",
                  opacity: isHovered ? 0.9 : 1,
                }}
                onMouseEnter={() => setHoveredId(event.id)}
                onMouseLeave={() => setHoveredId(null)}
              >
                <div className="flex items-center justify-between gap-2.5">
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-medium truncate" style={{ color: "var(--hexa-text-primary)" }}>
                      {event.title}
                    </div>
                    <div className="text-xs truncate mt-0.5" style={{ color: "var(--hexa-text-secondary)" }}>
                      {event.subtitle}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="text-xs tabular-nums" style={{ color: "var(--hexa-text-secondary)" }}>
                      {event.time}
                    </span>
                    <ChevronRight className="w-3.5 h-3.5" style={{ color: "var(--hexa-purple)" }} />
                  </div>
                </div>
              </Link>
            )
          })
        )}
      </div>
    </div>
  )
}
